import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  Slider,
  InputAdornment,
  Drawer,
  Divider,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import apiClient from '../../../request/http-common';
import { setTradings } from '../../../request/Analysis/hitorySlice';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#1E2329',
    color: 'white',
    fontSize: '12px',
    height: '32px',
    '& fieldset': { borderColor: '#2B3139' },
    '&:hover fieldset': { borderColor: '#F7A600' },
    '&.Mui-focused fieldset': { borderColor: '#F7A600' }
  },
  '& .MuiInputLabel-root': { 
    color: '#848E9C', 
    fontSize: '11px',
    '&.Mui-focused': { color: '#F7A600' }
  },
  '& .MuiOutlinedInput-input': {
    padding: '6px 12px',
    fontSize: '12px'
  }
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: '#1E2329',
  color: 'white',
  fontSize: '12px',
  height: '32px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2B3139' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600' },
  '& .MuiSvgIcon-root': { color: '#848E9C' },
  '& .MuiSelect-select': {
    padding: '6px 12px',
    fontSize: '12px'
  }
}));

const QuickButton = styled(Button)(({ theme }) => ({
  minWidth: '32px',
  height: '24px',
  padding: '2px 8px',
  fontSize: '10px',
  backgroundColor: '#2B3139',
  color: '#848E9C',
  border: '1px solid #2B3139',
  '&:hover': {
    backgroundColor: '#F7A600',
    color: '#000',
    borderColor: '#F7A600'
  }
}));

const TrailingStop = ({ orderType, balance, coinPrice, currentPair, tradingType }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tradingPlans = useSelector((state) => state.plans.tradingPlans);
  const currentPairFromState = useSelector((state) => state.currentPair.currentPair);
  const pair = currentPair || currentPairFromState;

  // Form state
  const [retracement, setRetracement] = useState('Percentage');
  const [retracementValue, setRetracementValue] = useState('');
  const [orderValue, setOrderValue] = useState('');
  const [activationPrice, setActivationPrice] = useState(false);
  const [activationPriceValue, setActivationPriceValue] = useState('');
  const [orderValueSlider, setOrderValueSlider] = useState(0);
  const [duration, setDuration] = useState('');
  const [error, setError] = useState('');
  
  // Confirmation drawer state
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [pendingOrderSide, setPendingOrderSide] = useState('');

  // Leverage and margin for futures
  const [leverage, setLeverage] = useState('10');
  const [marginType, setMarginType] = useState('Cross');

  // Utility functions
  const formatPrice = (value) => parseFloat(value || 0).toFixed(2);
  const formatQuantity = (value) => parseFloat(value || 0).toFixed(4);
  const formatBalance = (value) => parseFloat(value || 0).toFixed(2);

  const retracementButtons = ['5%', '10%'];

  const calculateMaxBuy = () => {
    if (!balance || !coinPrice) return '0.0000';
    const maxQty = parseFloat(balance) / parseFloat(coinPrice);
    return formatQuantity(maxQty);
  };

  const calculateOrderValue = () => {
    const orderValueNum = parseFloat(orderValue || 0);
    return formatPrice(orderValueNum);
  };

  // Update order value when amount changes
  useEffect(() => {
    // This will trigger re-render when orderValue changes
  }, [orderValue]);

  const handleOrderValueSliderChange = (event, newValue) => {
    setOrderValueSlider(newValue);
    if (balance) {
      const newOrderValue = (parseFloat(balance) * newValue) / 100;
      setOrderValue(formatPrice(newOrderValue));
    }
  };

  const handleRetracementButtonClick = (percentage) => {
    setRetracementValue(percentage.replace('%', ''));
  };

  const handlePercentageClick = (percentage) => {
    if (balance) {
      const newOrderValue = (parseFloat(balance) * percentage) / 100;
      setOrderValue(formatPrice(newOrderValue));
      setOrderValueSlider(percentage);
    }
  };

  // Submit mutation
  const { isLoading: isSubmitting, mutate: submitOrder } = useMutation(
    async (orderData) => {
      console.log('Submitting Trailing Stop order:', orderData);
      return await apiClient.post(`/api/start-trading`, orderData);
    },
    {
      onSuccess: (res) => {
        console.log('Trailing Stop order response:', res.data);
        if (res.data.status === 'success') {
          dispatch(setTradings(res.data.tradings));
          localStorage.setItem('notification', JSON.stringify({
            message: `${pendingOrderSide === 'buy' ? 'Buy' : 'Sell'} Trailing Stop order placed successfully!`,
            'type': 'success'
          }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
          
          // Reset form
          setRetracementValue('');
          setOrderValue('');
          setActivationPriceValue('');
          setOrderValueSlider(0);
          setDuration('');
          setError('');
          setConfirmationOpen(false);
          setPendingOrderSide('');
          
          // Redirect to trade history
          navigate('/trade-history');
        }
      },
      onError: (err) => {
        console.error('Trailing Stop order error:', err);
        if (err.response?.data?.errors) {
          setError(Object.values(err.response.data.errors).flat().join(', '));
        } else {
          let errorMessage = err.response?.data?.message || 'Failed to place Trailing Stop order';
          setError(errorMessage);
          localStorage.setItem('notification', JSON.stringify({
            message: errorMessage,
            'type': 'error'
          }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
        }
        setConfirmationOpen(false);
      },
    }
  );

  const validateOrder = () => {
    if (!retracementValue || parseFloat(retracementValue) <= 0) {
      setError('Please enter a valid retracement value');
      return false;
    }
    
    if (!orderValue || parseFloat(orderValue) <= 0) {
      setError('Please enter a valid order value');
      return false;
    }
    
    if (!duration) {
      setError('Please select a duration');
      return false;
    }

    if (activationPrice && (!activationPriceValue || parseFloat(activationPriceValue) <= 0)) {
      setError('Please enter a valid activation price');
      return false;
    }
    
    const orderValueNum = parseFloat(orderValue);
    const balanceNum = parseFloat(balance);
    
    if (orderValueNum > balanceNum) {
      setError(`Insufficient balance. Required: ${orderValue} USDT, Available: ${formatBalance(balance)} USDT`);
      return false;
    }
    
    return true;
  };

  const handleOrderClick = (side) => {
    console.log('Trailing Stop order click:', side);
    setError('');
    
    if (!validateOrder()) {
      return;
    }
    
    setPendingOrderSide(side);
    setConfirmationOpen(true);
  };

  const confirmOrder = () => {
    console.log('Confirming Trailing Stop order...');
    
    const orderData = {
      // Basic order fields
      amount: parseFloat(orderValue),
      stop_loss: 0,
      take_profit: 0,
      timer_units: parseInt(duration),
      trading_pair: pair,
      order_type: 'Trailing Stop',
      pair: pair,
      entry_point: parseFloat(coinPrice).toFixed(6),
      side: pendingOrderSide,
      
      // Advanced order fields
      trading_type: tradingType || 'Spot',
      retracement_type: retracement,
      retracement_value: parseFloat(retracementValue),
      activation_price: activationPrice ? parseFloat(activationPriceValue) : null,
      
      // Futures specific
      leverage: tradingType === 'Futures' ? parseFloat(leverage) : null,
      margin_type: tradingType === 'Futures' ? marginType : null,
    };
    
    console.log('Final Trailing Stop order data:', orderData);
    submitOrder(orderData);
  };

  const getSelectedPlan = () => {
    if (!tradingPlans || !Array.isArray(tradingPlans)) {
      return null;
    }
    return tradingPlans.find(plan => plan.id.toString() === duration.toString());
  };

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5, fontSize: '11px' }}>
          {error}
        </Alert>
      )}

      {/* Available Balance */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, px: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px' }}>
          Available
        </Typography>
        <Typography variant="caption" sx={{ color: 'white', fontSize: '11px', fontWeight: 500 }}>
          {formatBalance(balance)} USDT
        </Typography>
      </Box>

      {/* Cross/Isolated and Leverage - Only for Futures */}
      {tradingType === 'Futures' && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <StyledSelect 
            size="small" 
            value={marginType}
            onChange={(e) => setMarginType(e.target.value)}
            sx={{ flex: 1 }}
          >
            <MenuItem value="Cross">Cross</MenuItem>
            <MenuItem value="Isolated">Isolated</MenuItem>
          </StyledSelect>
          <StyledSelect 
            size="small" 
            value={leverage}
            onChange={(e) => setLeverage(e.target.value)}
            sx={{ flex: 1 }}
          >
            <MenuItem value="10">10x</MenuItem>
            <MenuItem value="20">20x</MenuItem>
            <MenuItem value="50">50x</MenuItem>
            <MenuItem value="100">100x</MenuItem>
          </StyledSelect>
        </Box>
      )}

      {/* Duration Selection */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Duration
        </Typography>
        <FormControl fullWidth size="small">
          <StyledSelect
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              Select Duration
            </MenuItem>
            {tradingPlans && Array.isArray(tradingPlans) && tradingPlans.length > 0 && tradingPlans.map(plan => (
              <MenuItem key={plan.id} value={plan.id}>
                {plan.duration}
              </MenuItem>
            ))}
          </StyledSelect>
        </FormControl>
      </Box>

      {/* Retracement */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Retracement
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
          <StyledSelect
            value={retracement}
            onChange={(e) => setRetracement(e.target.value)}
          >
            <MenuItem value="Percentage">Percentage</MenuItem>
            <MenuItem value="Amount">Amount</MenuItem>
          </StyledSelect>
        </FormControl>
        
        {/* Retracement Quick Buttons */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1, alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mr: 0.5 }}>
            Retrace...
          </Typography>
          {retracementButtons.map(percentage => (
            <QuickButton
              key={percentage}
              onClick={() => handleRetracementButtonClick(percentage)}
            >
              {percentage}
            </QuickButton>
          ))}
        </Box>

        <StyledTextField
          fullWidth
          size="small"
          value={retracementValue}
          onChange={(e) => setRetracementValue(e.target.value)}
          placeholder="Enter percentage"
          InputProps={{
            endAdornment: retracement === 'Percentage' && (
              <InputAdornment position="end">
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>%</Typography>
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Order Value */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Order Value
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <StyledTextField
            fullWidth
            size="small"
            value={orderValue}
            onChange={(e) => setOrderValue(e.target.value)}
            placeholder="0.00"
          />
          <Box sx={{ 
            bgcolor: '#2B3139', 
            borderRadius: 1, 
            p: 1, 
            minWidth: '60px',
            textAlign: 'center',
            border: '1px solid #2B3139'
          }}>
            <Typography sx={{ color: 'white', fontSize: '11px' }}>USDT</Typography>
          </Box>
        </Box>
        
        <Slider
          value={orderValueSlider}
          onChange={handleOrderValueSliderChange}
          min={0}
          max={100}
          size="small"
          sx={{
            color: '#F7A600',
            height: 4,
            '& .MuiSlider-track': { backgroundColor: '#F7A600' },
            '& .MuiSlider-thumb': { 
              backgroundColor: '#F7A600',
              width: 12,
              height: 12,
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 8px rgba(247, 166, 0, 0.16)'
              }
            }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          {[0, 25, 50, 75, 100].map(value => (
            <Typography 
              key={value} 
              variant="caption" 
              sx={{ 
                color: '#848E9C', 
                fontSize: '9px',
                cursor: 'pointer',
                '&:hover': { color: '#F7A600' }
              }}
              onClick={() => handlePercentageClick(value)}
            >
              {value}%
            </Typography>
          ))}
        </Box>
      </Box>

      {/* Activation Price Checkbox */}
      <FormControlLabel
        control={
          <Checkbox
            checked={activationPrice}
            onChange={(e) => setActivationPrice(e.target.checked)}
            size="small"
            sx={{ 
              color: '#848E9C',
              '&.Mui-checked': { color: '#F7A600' },
              padding: '4px'
            }}
          />
        }
        label={
          <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>
            Activation Price
          </Typography>
        }
        sx={{ mb: 1.5, ml: 0 }}
      />

      {/* Activation Price Input */}
      {activationPrice && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
            Activation Price (USDT)
          </Typography>
          <StyledTextField
            fullWidth
            size="small"
            value={activationPriceValue}
            onChange={(e) => setActivationPriceValue(e.target.value)}
            placeholder="0.00"
          />
        </Box>
      )}

      {/* Order Summary */}
      <Box sx={{ 
        bgcolor: '#181A20', 
        borderRadius: 1, 
        p: 1, 
        mb: 1.5,
        border: '1px solid #2B3139'
      }}>
        {tradingType === 'Spot' && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
              Max Buy
            </Typography>
            <Typography variant="caption" sx={{ color: '#0ECB81', fontSize: '10px', fontWeight: 500 }}>
              {calculateMaxBuy()} {pair}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            Order Value
          </Typography>
          <Typography variant="caption" sx={{ color: '#0ECB81', fontSize: '10px', fontWeight: 500 }}>
            {calculateOrderValue()} USDT
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            Duration
          </Typography>
          <Typography variant="caption" sx={{ color: '#0ECB81', fontSize: '10px', fontWeight: 500 }}>
            {getSelectedPlan()?.duration || 'N/A'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            Est. Fee
          </Typography>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            {formatPrice(parseFloat(orderValue || 0) * 0.001)} USDT
          </Typography>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => handleOrderClick('buy')}
          disabled={isSubmitting}
          sx={{
            bgcolor: orderType === 'Buy' ? '#0ECB81' : '#1E2329',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            py: 1,
            height: '36px',
            '&:hover': { 
              bgcolor: orderType === 'Buy' ? '#0BB76D' : '#2A2E39'
            },
            '&:disabled': {
              bgcolor: '#2B3139',
              color: '#848E9C'
            }
          }}
        >
          {isSubmitting ? 'Processing...' : `${tradingType === 'Futures' ? 'Long' : 'Buy'} ${pair}`}
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={() => handleOrderClick('sell')}
          disabled={isSubmitting}
          sx={{
            bgcolor: orderType === 'Sell' ? '#F6465D' : '#1E2329',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            py: 1,
            height: '36px',
            '&:hover': { 
              bgcolor: orderType === 'Sell' ? '#E13B52' : '#2A2E39'
            },
            '&:disabled': {
              bgcolor: '#2B3139',
              color: '#848E9C'
            }
          }}
        >
          {isSubmitting ? 'Processing...' : `${tradingType === 'Futures' ? 'Short' : 'Sell'} ${pair}`}
        </Button>
      </Box>

      {/* Confirmation Drawer */}
      <Drawer
        anchor="bottom"
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#181A20',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '80vh',
            zIndex: 99999,
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
          }
        }}
        ModalProps={{
          sx: {
            zIndex: 99999,
          }
        }}
        sx={{
          zIndex: 99999,
          '& .MuiDrawer-paper': {
            zIndex: 99999,
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ color: 'white', mb: 2, textAlign: 'center' }}>
            Confirm Trailing Stop Order
          </Typography>
          
          <Box sx={{ bgcolor: '#1E2329', borderRadius: 1, p: 1.5, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Order Type:</Typography>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                Trailing Stop
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Side:</Typography>
              <Typography variant="body2" sx={{ 
                color: pendingOrderSide === 'buy' ? '#0ECB81' : '#F6465D', 
                fontWeight: 500 
              }}>
                {pendingOrderSide === 'buy' ? 'Buy' : 'Sell'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Pair:</Typography>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {pair}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Order Value:</Typography>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {calculateOrderValue()} USDT
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Retracement:</Typography>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {retracementValue} {retracement === 'Percentage' ? '%' : 'USDT'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Duration:</Typography>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {getSelectedPlan()?.duration || 'N/A'}
              </Typography>
            </Box>
            {activationPrice && activationPriceValue && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#848E9C' }}>Activation Price:</Typography>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                  {activationPriceValue} USDT
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: 1, borderColor: '#2B3139' }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Est. Fee:</Typography>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>
                {formatPrice(parseFloat(orderValue || 0) * 0.001)} USDT
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setConfirmationOpen(false)}
              disabled={isSubmitting}
              sx={{
                borderColor: '#2B3139',
                color: '#848E9C',
                '&:hover': {
                  borderColor: '#F7A600',
                  color: '#F7A600'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={confirmOrder}
              disabled={isSubmitting}
              sx={{
                bgcolor: '#F7A600',
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#E69500'
                },
                '&:disabled': {
                  bgcolor: '#2B3139',
                  color: '#848E9C'
                }
              }}
            >
              {isSubmitting ? 'Processing...' : 'Confirm Order'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default TrailingStop;
